import React, { useState, useEffect } from 'react'
import { Calendar, Clock, User, CreditCard, CheckCircle, AlertCircle } from 'lucide-react'
import { GuruProfile, Session, GuruConnectApi } from '../../services/guruConnectApi'

interface SessionBookingProps {
  guru: GuruProfile
  onClose: () => void
  onSuccess: (session: Session) => void
}

interface TimeSlot {
  time: string
  available: boolean
  formatted: string
}

export const SessionBooking: React.FC<SessionBookingProps> = ({ guru, onClose, onSuccess }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string>('')
  const [sessionType, setSessionType] = useState('consultation')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [duration, setDuration] = useState(60)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([])

  const sessionTypes = [
    { id: 'consultation', label: 'Initial Consultation', duration: 60 },
    { id: 'follow_up', label: 'Follow-up Session', duration: 45 },
    { id: 'assessment', label: 'Health Assessment', duration: 90 },
    { id: 'coaching', label: 'Coaching Session', duration: 60 },
    { id: 'emergency', label: 'Emergency Consultation', duration: 30 },
  ]

  // Generate calendar dates for next 30 days
  const generateCalendarDates = () => {
    const dates = []
    const today = new Date()
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      dates.push(date)
    }
    
    return dates
  }

  // Generate time slots for a given date
  const generateTimeSlots = (date: Date): TimeSlot[] => {
    const slots: TimeSlot[] = []
    const dayOfWeek = date.getDay()
    
    // Mock availability - in real app, this would come from the API
    const availableHours = {
      0: [], // Sunday
      1: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'], // Monday
      2: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'], // Tuesday
      3: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'], // Wednesday
      4: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'], // Thursday
      5: ['09:00', '10:00', '11:00', '14:00', '15:00'], // Friday
      6: ['10:00', '11:00', '14:00', '15:00'], // Saturday
    }

    const hours = availableHours[dayOfWeek as keyof typeof availableHours] || []
    
    hours.forEach(hour => {
      slots.push({
        time: hour,
        available: true,
        formatted: new Date(`2000-01-01T${hour}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      })
    })

    return slots
  }

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setSelectedTime('')
    const slots = generateTimeSlots(date)
    setAvailableSlots(slots)
  }

  // Handle session booking
  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || !title) {
      setError('Please fill in all required fields')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Combine date and time
      const [hours, minutes] = selectedTime.split(':')
      const bookingDate = new Date(selectedDate)
      bookingDate.setHours(parseInt(hours), parseInt(minutes), 0, 0)

      const sessionData = {
        guru_id: guru.id,
        session_type: sessionType,
        title,
        description: description || undefined,
        scheduled_at: bookingDate.toISOString(),
        duration_minutes: duration,
      }

      const session = await GuruConnectApi.createSession(sessionData)
      onSuccess(session)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book session')
    } finally {
      setLoading(false)
    }
  }

  // Calculate session cost
  const sessionCost = (guru.session_rate / 60) * duration

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Book Session with {GuruConnectApi.getGuruDisplayName(guru)}
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                {GuruConnectApi.getGuruRoleDisplay(guru.guru_role)} • {guru.specialties.join(', ')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Calendar and Time Selection */}
            <div className="space-y-6">
              {/* Session Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Session Type
                </label>
                <select
                  value={sessionType}
                  onChange={(e) => {
                    setSessionType(e.target.value)
                    const selected = sessionTypes.find(s => s.id === e.target.value)
                    if (selected) setDuration(selected.duration)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  {sessionTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.label} ({type.duration} min)
                    </option>
                  ))}
                </select>
              </div>

              {/* Calendar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Date
                </label>
                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                  {generateCalendarDates().map((date, index) => {
                    const isSelected = selectedDate?.toDateString() === date.toDateString()
                    const isToday = date.toDateString() === new Date().toDateString()
                    const isPast = date < new Date()
                    
                    return (
                      <button
                        key={index}
                        onClick={() => !isPast && handleDateSelect(date)}
                        disabled={isPast}
                        className={`
                          p-2 text-sm rounded-md transition-colors
                          ${isSelected 
                            ? 'bg-blue-600 text-white' 
                            : isToday 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                              : isPast 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                          }
                        `}
                      >
                        {date.getDate()}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Time
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((slot, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedTime(slot.time)}
                        disabled={!slot.available}
                        className={`
                          p-3 text-sm rounded-md border transition-colors
                          ${selectedTime === slot.time
                            ? 'bg-blue-600 text-white border-blue-600'
                            : slot.available
                              ? 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 text-gray-900 dark:text-white'
                              : 'border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
                          }
                        `}
                      >
                        {slot.formatted}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Session Details and Booking */}
            <div className="space-y-6">
              {/* Session Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Session Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Initial Diabetes Consultation"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of what you'd like to discuss..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Session Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-white">Session Summary</h3>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Duration:</span>
                  <span className="text-gray-900 dark:text-white">{duration} minutes</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Rate:</span>
                  <span className="text-gray-900 dark:text-white">{GuruConnectApi.formatPrice(guru.session_rate)}/hour</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Total Cost:</span>
                  <span className="text-lg font-semibold text-green-600">
                    {GuruConnectApi.formatPrice(sessionCost)}
                  </span>
                </div>

                {selectedDate && selectedTime && (
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {selectedDate.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300 mt-1">
                      <Clock className="w-4 h-4" />
                      <span>
                        {new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center space-x-2 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}

              {/* Booking Button */}
              <button
                onClick={handleBooking}
                disabled={loading || !selectedDate || !selectedTime || !title}
                className={`
                  w-full py-3 px-4 rounded-md font-medium transition-colors
                  ${loading || !selectedDate || !selectedTime || !title
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }
                `}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Booking...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <CreditCard className="w-4 h-4" />
                    <span>Book Session ({GuruConnectApi.formatPrice(sessionCost)})</span>
                  </div>
                )}
              </button>

              {/* Guru Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <img
                    src={GuruConnectApi.getGuruAvatar(guru)}
                    alt={GuruConnectApi.getGuruDisplayName(guru)}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {GuruConnectApi.getGuruDisplayName(guru)}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {GuruConnectApi.getGuruRoleDisplay(guru.guru_role)}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex items-center space-x-1">
                        <span className="text-yellow-400">★</span>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {guru.average_rating.toFixed(1)} ({guru.total_reviews} reviews)
                        </span>
                      </div>
                      {guru.is_verified && (
                        <div className="flex items-center space-x-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-green-600 dark:text-green-400">Verified</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 